import { useMemo } from "react";

/**
 * Calculate password strength based on various criteria.
 * Returns strength level: "weak", "medium", or "strong"
 *
 * Criteria:
 * - Length >= 8: +1 point
 * - Length >= 12: +1 point
 * - Mixed case (both upper and lower): +1 point
 * - Contains numbers: +1 point
 * - Contains special characters: +1 point
 *
 * Score:
 * - 0-2: weak
 * - 3-4: medium
 * - 5: strong
 *
 * @param {string} password - The password to evaluate
 * @returns {object} { strength: "weak"|"medium"|"strong", score: number }
 *
 * @example
 * const { strength, score } = usePasswordStrength(password);
 * // strength = "medium", score = 3
 */
export const usePasswordStrength = (password) => {
  const result = useMemo(() => {
    if (!password || password.length === 0) {
      return { strength: "weak", score: 0 };
    }

    let score = 0;

    // Length checks
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;

    // Character variety checks
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    // Determine strength level
    let strength;
    if (score <= 2) strength = "weak";
    else if (score <= 4) strength = "medium";
    else strength = "strong";

    return { strength, score };
  }, [password]);

  return result;
};
